import { ConversationContext, ConversationMessage, RefundStatus } from './types';
import { BehaviorDetector } from './behavior-detector';

export class ConversationAnalyzer {
  static analyzeConversation(
    messages: ConversationMessage[],
    platformCategory: string,
    platform: string,
    userIssue: string,
    timeElapsedDays: number = 0
  ): ConversationContext {
    
    const latestSellerMessage = this.getLatestSellerMessage(messages);
    const initialRequest = this.getInitialAIMessage(messages);
    
    let behaviorAnalysis = null;
    if (latestSellerMessage) {
      behaviorAnalysis = BehaviorDetector.detectBehavior(latestSellerMessage.message);
    }

    const context: ConversationContext = {
      platform_category: platformCategory,
      platform: platform,
      user_issue_description: userIssue,
      initial_request_sent: !!initialRequest,
      initial_request_template: this.detectRequestTemplate(userIssue, platformCategory),
      seller_response: latestSellerMessage?.message,
      detected_seller_behavior: behaviorAnalysis?.behavior,
      refund_status: this.determineRefundStatus(messages, behaviorAnalysis),
      escalation_stage: this.calculateEscalationStage(messages, behaviorAnalysis),
      user_sent_proof: this.hasUserProvidedProof(messages),
      platform_policy_applied: this.isPlatformPolicyApplied(platform, behaviorAnalysis),
      policy_note: this.generatePolicyNote(platform, platformCategory, behaviorAnalysis),
      follow_up_needed: this.determineFollowUpNeed(behaviorAnalysis, timeElapsedDays),
      time_elapsed_days: timeElapsedDays,
      conversation_thread: messages,
      next_recommended_action: behaviorAnalysis?.suggested_response_strategy,
      escalation_confidence: behaviorAnalysis?.confidence
    };

    return context;
  }

  private static getLatestSellerMessage(messages: ConversationMessage[]): ConversationMessage | null {
    const sellerMessages = messages.filter(m => m.role === 'seller');
    return sellerMessages.length > 0 ? sellerMessages[sellerMessages.length - 1] : null;
  }

  private static getInitialAIMessage(messages: ConversationMessage[]): ConversationMessage | null {
    return messages.find(m => m.role === 'ai') || null;
  }

  private static detectRequestTemplate(userIssue: string, platformCategory: string): any {
    const issue = userIssue.toLowerCase();
    
    // Flight-related patterns
    if (issue.includes('flight') || issue.includes('airline') || issue.includes('eu261')) {
      return 'eu261_cancellation';
    }
    
    // Delivery issues
    if (issue.includes('never received') || issue.includes('not delivered') || issue.includes('didn\'t arrive')) {
      return 'non_delivery';
    }
    
    // Platform-specific templates
    if (platformCategory === 'gig_platforms') {
      if (issue.includes('milestone') || issue.includes('incomplete work')) {
        return 'milestone_dispute';
      }
    }
    
    if (platformCategory === 'event_ticketing') {
      if (issue.includes('canceled') || issue.includes('cancelled') || issue.includes('event')) {
        return 'event_cancellation';
      }
    }
    
    if (platformCategory === 'subscription') {
      if (issue.includes('subscription') || issue.includes('recurring charge')) {
        return 'subscription_cancellation';
      }
    }
    
    if (platformCategory === 'accommodation') {
      if (issue.includes('property') || issue.includes('hotel') || issue.includes('booking')) {
        return 'accommodation_issue';
      }
    }
    
    // Default fallbacks
    if (issue.includes('quality') || issue.includes('not as described')) {
      return 'quality_dispute';
    }
    
    if (issue.includes('charged') || issue.includes('unauthorized')) {
      return 'unauthorized_charge';
    }
    
    return 'service_quality';
  }

  private static determineRefundStatus(messages: ConversationMessage[], behaviorAnalysis: any): RefundStatus {
    if (!behaviorAnalysis) return 'pending';
    
    switch (behaviorAnalysis.behavior) {
      case 'full_acceptance':
        return 'approved';
      case 'partial_compromise':
        return 'partial';
      case 'refusal_due_to_tracking':
      case 'policy_citation':
      case 'contract_enforcement':
        return 'denied';
      case 'stalling':
      case 'deflection':
        return 'escalated';
      default:
        return 'pending';
    }
  }

  private static calculateEscalationStage(messages: ConversationMessage[], behaviorAnalysis: any): number {
    const exchangeCount = messages.filter(m => m.role === 'seller').length;
    
    if (!behaviorAnalysis) return 0;
    
    if (behaviorAnalysis.escalation_recommended) {
      return Math.min(exchangeCount + 1, 3);
    }
    
    return Math.max(1, exchangeCount);
  }

  private static hasUserProvidedProof(messages: ConversationMessage[]): boolean {
    const userMessages = messages.filter(m => m.role === 'user');
    return userMessages.some(m => 
      m.message.toLowerCase().includes('proof') ||
      m.message.toLowerCase().includes('evidence') ||
      m.message.toLowerCase().includes('screenshot') ||
      m.message.toLowerCase().includes('receipt')
    );
  }

  private static isPlatformPolicyApplied(platform: string, behaviorAnalysis: any): boolean {
    if (!behaviorAnalysis) return false;
    
    return behaviorAnalysis.behavior === 'policy_citation' || 
           behaviorAnalysis.behavior === 'contract_enforcement';
  }

  private static generatePolicyNote(platform: string, platformCategory: string, behaviorAnalysis: any): string | undefined {
    if (!behaviorAnalysis) return undefined;
    
    const policyNotes: Record<string, Record<string, string>> = {
      'ecommerce': {
        'Amazon': 'Amazon A-to-Z Guarantee may still cover this case.',
        'eBay': 'eBay Money Back Guarantee applies to most transactions.',
        'Walmart': 'Walmart\'s return policy may override seller policies.'
      },
      'gig_platforms': {
        'Upwork': 'Upwork Work Diary and dispute resolution can be leveraged.',
        'Fiverr': 'Fiverr\'s Resolution Center protects buyer interests.',
        'Freelancer': 'Freelancer Milestone Protection applies to project disputes.'
      },
      'event_ticketing': {
        'Ticketmaster': 'Fan Protection and event change policies may apply.',
        'StubHub': 'FanProtect Guarantee covers invalid or problematic tickets.'
      },
      'accommodation': {
        'Airbnb': 'Host Guarantee covers significant amenity failures.',
        'Booking.com': 'Guest Review system and protection policies apply.'
      }
    };
    
    return policyNotes[platformCategory]?.[platform];
  }

  private static determineFollowUpNeed(behaviorAnalysis: any, timeElapsedDays: number): boolean {
    if (!behaviorAnalysis) return true;
    
    if (behaviorAnalysis.behavior === 'full_acceptance') return false;
    if (behaviorAnalysis.behavior === 'stalling' && timeElapsedDays > 3) return true;
    if (behaviorAnalysis.escalation_recommended) return true;
    
    return behaviorAnalysis.behavior !== 'full_acceptance';
  }
}